import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Award, Plus, Pencil, Trash2, UploadCloud, FileCheck2, EyeOff, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from '../../lib/toast';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from '../organization/finance/shared';

const STATUS_LABELS = { unverified: 'غير موثقة', pending_review: 'قيد المراجعة', verified: 'موثقة', rejected: 'مرفوضة', expired: 'منتهية' };
const STATUS_COLORS = { unverified: 'bg-slate-100 text-slate-500 ring-slate-200', pending_review: 'bg-amber-50 text-amber-700 ring-amber-200', verified: 'bg-teal-50 text-teal-700 ring-teal-200', rejected: 'bg-red-50 text-red-600 ring-red-200', expired: 'bg-slate-100 text-slate-500 ring-slate-200' };
const EMPTY = { name: '', issuing_authority: '', level: '', certificate_number: '', issue_date: '', expiry_date: '', show_in_public: true };

const daysLeft = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);

export default function CoachCertificationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/coaches/profile/me/certifications`, auth()); setItems(res.data); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: items.length,
    verified: items.filter((c) => c.verification_status === 'verified').length,
    expiring: items.filter((c) => { const d = daysLeft(c.expiry_date); return d !== null && d >= 0 && d <= 90; }).length,
  }), [items]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setModal(true); };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/coaches/documents/upload`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, proof_document_id: res.data.document_id }));
      toast.success('تم رفع إثبات الشهادة');
    } catch (err) { toast.error(errMsg(err, 'فشل الرفع')); }
    setUploading(false);
  };

  const viewProof = async (id) => {
    try {
      const res = await axios.get(`${API}/coaches/documents/${id}`, { ...auth(), responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (err) { toast.error(errMsg(err, 'تعذّر فتح الملف')); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, issue_date: form.issue_date || null, expiry_date: form.expiry_date || null };
    try {
      if (editing) await axios.put(`${API}/coaches/profile/me/certifications/${editing.id}`, payload, auth());
      else await axios.post(`${API}/coaches/profile/me/certifications`, payload, auth());
      toast.success('تم الحفظ — ستُراجع من فريق الإشراف');
      setModal(false); load();
    } catch (err) { toast.error(errMsg(err, 'فشل الحفظ')); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/coaches/profile/me/certifications/${id}`, auth()); toast.success('تم الحذف'); setConfirmId(null); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل الحذف')); }
  };

  return (
    <div className="space-y-4" data-testid="coach-certifications-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md shadow-amber-500/20"><Award size={19} strokeWidth={2.2} /></span>
          <div>
            <h3 className="text-base font-bold text-slate-800">الشهادات والرخص التدريبية</h3>
            <p className="mt-0.5 text-xs text-slate-500" data-testid="coach-cert-stats">
              {stats.total} شهادة · {stats.verified} موثقة{stats.expiring ? ` · ${stats.expiring} قريبة الانتهاء` : ''}
            </p>
          </div>
        </div>
        <PrimaryBtn onClick={openNew} data-testid="coach-cert-add-btn"><Plus size={16} /> إضافة شهادة</PrimaryBtn>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center" data-testid="coach-cert-empty">
          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><Award size={22} /></span>
          <p className="text-sm font-bold text-slate-700">لا توجد شهادات مضافة بعد</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">أضف رخصك التدريبية (مثل رخصة C / B / A) مع ملف الإثبات لتزيد ثقة المنشآت بملفك.</p>
          <button onClick={openNew} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#16233b] px-5 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-95" data-testid="coach-cert-empty-add-btn"><Plus size={15} /> إضافة أول شهادة</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((c) => {
            const dl = daysLeft(c.expiry_date);
            const expiringSoon = dl !== null && dl >= 0 && dl <= 90;
            const expired = dl !== null && dl < 0;
            return (
              <div key={c.id} className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.07)]" data-testid="coach-cert-row">
                <span className={`absolute inset-y-0 start-0 w-1 ${c.verification_status === 'verified' ? 'bg-teal-500' : expired ? 'bg-slate-300' : 'bg-amber-400'}`} />
                <div className="flex items-start justify-between gap-3 ps-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-bold text-slate-800">{c.name}</h4>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${STATUS_COLORS[c.verification_status]}`}>
                        {c.verification_status === 'verified' && <ShieldCheck size={11} />}{STATUS_LABELS[c.verification_status]}
                      </span>
                      {!c.show_in_public && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500"><EyeOff size={11} /> مخفية</span>}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {c.issuing_authority}{c.level ? ` · ${c.level}` : ''}{c.certificate_number ? ` · رقم ${c.certificate_number}` : ''}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                      {c.issue_date && <span className="rounded-md bg-slate-50 px-2 py-1">صدرت {fmtDate(c.issue_date)}</span>}
                      {c.expiry_date && (
                        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold ${expired ? 'bg-red-50 text-red-600' : expiringSoon ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-500'}`}>
                          {(expired || expiringSoon) && <AlertTriangle size={11} />}
                          {expired ? `منتهية منذ ${fmtDate(c.expiry_date)}` : expiringSoon ? `تنتهي بعد ${dl} يوم` : `تنتهي ${fmtDate(c.expiry_date)}`}
                        </span>
                      )}
                      {c.proof_document_id && (
                        <button onClick={() => viewProof(c.proof_document_id)} className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-1 font-bold text-teal-700 transition-colors hover:bg-teal-100" data-testid="coach-cert-view-proof-btn">
                          <FileCheck2 size={11} /> عرض الإثبات
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button onClick={() => openEdit(c)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-teal-300 hover:text-teal-600" data-testid="coach-cert-edit-btn"><Pencil size={14} /></button>
                    <button onClick={() => setConfirmId(c.id)} className="rounded-lg border border-slate-200 bg-white p-2 text-red-500 transition-colors hover:border-red-300" data-testid="coach-cert-delete-btn"><Trash2 size={14} /></button>
                  </div>
                </div>
                {confirmId === c.id && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-red-50 p-2.5 ps-3" data-testid="coach-cert-delete-confirm">
                    <span className="text-xs font-semibold text-red-700">تأكيد حذف هذه الشهادة؟</span>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmId(null)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600">إلغاء</button>
                      <button onClick={() => remove(c.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="coach-cert-delete-confirm-btn">حذف</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={modal} title={editing ? 'تعديل شهادة' : 'إضافة شهادة/رخصة'} onClose={() => setModal(false)} wide>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم الشهادة" required><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coach-cert-input-name" /></Field>
          <Field label="الجهة المانحة" required><input required className={inputCls} value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} data-testid="coach-cert-input-authority" /></Field>
          <Field label="المستوى"><input className={inputCls} value={form.level || ''} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="رخصة B" /></Field>
          <Field label="رقم الشهادة (اختياري)"><input className={inputCls} value={form.certificate_number || ''} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} /></Field>
          <Field label="تاريخ الإصدار"><input type="date" className={inputCls} value={form.issue_date || ''} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
          <Field label="تاريخ الانتهاء"><input type="date" className={inputCls} value={form.expiry_date || ''} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3">
            <span className="mb-2 block text-xs font-semibold text-slate-600">ملف الإثبات (PDF أو صورة)</span>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition-all hover:border-teal-300 active:scale-95">
                <UploadCloud size={15} />
                {uploading ? 'جارِ الرفع...' : form.proof_document_id ? 'تحديث الملف المرفوع' : 'رفع ملف الإثبات'}
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadProof} data-testid="coach-cert-proof-input" />
              </label>
              {form.proof_document_id && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-bold text-teal-700 ring-1 ring-teal-200"><FileCheck2 size={12} /> الملف مرفوع</span>}
            </div>
          </div>
          <label className="col-span-full flex w-fit cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-teal-300">
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={Boolean(form.show_in_public)} onChange={(e) => setForm({ ...form, show_in_public: e.target.checked })} /> إظهار هذه الشهادة في الملف العام
          </label>
          <div className="col-span-full flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setModal(false)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="coach-cert-submit-btn">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
